import React, { useState, useEffect } from 'react';
import { Settings, Save, ToggleLeft, ToggleRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateConfig({ embedded = false }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/templates/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate({...template});
    setEditorOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/templates/${selectedTemplate.id}`,
        {
          subject: selectedTemplate.subject_template,
          html_body: selectedTemplate.body_html_template,
          trigger_days_before: selectedTemplate.trigger_days_before
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Plantilla actualizada exitosamente');
      setEditorOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error al guardar plantilla');
    }
  };

  const handleToggle = async (templateId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/templates/${templateId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newStatus = !currentStatus;
      toast.success(newStatus ? 'Plantilla activada' : 'Plantilla desactivada');
      fetchTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error('Error al cambiar estado');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header - solo mostrar si no está embebido */}
      {!embedded && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Button
                  onClick={() => navigate('/email-outbox')}
                  variant="ghost"
                  size="sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver a Bandeja
                </Button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Settings className="h-8 w-8 text-indigo-600" />
                Configuración de Plantillas
              </h1>
              <p className="text-gray-600 mt-2">
                Personaliza los mensajes automáticos del sistema
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-blue-800 mb-2">
          💡 Variables disponibles
        </h3>
        <div className="text-sm text-blue-700 space-y-1">
          <div><code className="bg-blue-100 px-2 py-0.5 rounded">{'{{nombre}}'}</code> - Nombre del cliente</div>
          <div><code className="bg-blue-100 px-2 py-0.5 rounded">{'{{numero_poliza}}'}</code> - Número de póliza</div>
          <div><code className="bg-blue-100 px-2 py-0.5 rounded">{'{{fecha_vencimiento}}'}</code> - Fecha de vencimiento</div>
          <div><code className="bg-blue-100 px-2 py-0.5 rounded">{'{{dias_para_vencimiento}}'}</code> - Días restantes</div>
        </div>
      </div>

      {/* Lista de Plantillas */}
      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-12 text-slate-500">
            Cargando plantillas...
          </div>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{template.icon}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {template.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div>
                      <span className="text-xs font-medium text-slate-500">Asunto actual:</span>
                      <div className="text-sm text-slate-700 mt-1 p-2 bg-slate-50 rounded">
                        {template.subject_template || 'Sin configurar'}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      Se genera {template.trigger_days_before} días antes del evento
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <Button
                    onClick={() => handleToggle(template.id, template.is_active)}
                    variant="outline"
                    size="sm"
                    className={template.is_active ? 'text-green-600' : 'text-gray-500'}
                  >
                    {template.is_active ? (
                      <>
                        <ToggleRight className="h-4 w-4 mr-2" />
                        Activa
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-4 w-4 mr-2" />
                        Inactiva
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => handleEdit(template)}
                    variant="default"
                    size="sm"
                  >
                    Editar Plantilla
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Editor Modal */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{selectedTemplate?.icon}</span>
                <span>Editar: {selectedTemplate?.name}</span>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Días antes del evento:
              </label>
              <input
                type="number"
                value={selectedTemplate?.trigger_days_before || 30}
                onChange={(e) => setSelectedTemplate({
                  ...selectedTemplate,
                  trigger_days_before: parseInt(e.target.value)
                })}
                className="w-32 px-3 py-2 border border-slate-300 rounded-lg"
                min="0"
                max="90"
              />
              <p className="text-xs text-slate-500 mt-1">
                El sistema generará el mensaje este número de días antes
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Asunto del Email:
              </label>
              <input
                type="text"
                value={selectedTemplate?.subject_template || ''}
                onChange={(e) => setSelectedTemplate({
                  ...selectedTemplate,
                  subject_template: e.target.value
                })}
                placeholder="Tu póliza {{numero_poliza}} vence en {{dias_para_vencimiento}} días"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Contenido del Email (HTML):
              </label>
              <textarea
                value={selectedTemplate?.body_html_template || ''}
                onChange={(e) => setSelectedTemplate({
                  ...selectedTemplate,
                  body_html_template: e.target.value
                })}
                rows={15}
                placeholder="<p>Hola {{nombre}},</p><p>Tu póliza...</p>"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={() => setEditorOpen(false)} variant="outline">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="gap-2">
              <Save className="h-4 w-4" />
              Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
